import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import {
  LayoutDashboard, Scissors, UserCog, MapPin, Box, HeartPulse,
  Megaphone, BarChart3, Settings, LogOut, ChevronRight, Bell, User
} from 'lucide-react';

const menuItems = [
  { path: '/dashboard', icon: LayoutDashboard, key: 'dashboard', label: 'Dashboard', roles: ['admin', 'superadmin'] },
  { path: '/services', icon: Scissors, key: 'services', label: 'Services', roles: ['admin', 'staff'] },
  { path: '/staff', icon: UserCog, key: 'staff', label: 'Team', roles: ['admin', 'staff'] },
  { path: '/locations', icon: MapPin, key: 'locations', label: 'Locations', roles: ['admin'] },
  { path: '/resources', icon: Box, key: 'resources', label: 'Resources', roles: ['admin'] },
  { path: '/retention', icon: HeartPulse, key: 'retention', label: 'Retention', roles: ['admin'] },
  { path: '/marketing', icon: Megaphone, key: 'marketing', label: 'Marketing', roles: ['admin', 'staff'] },
  { path: '/reports', icon: BarChart3, key: 'reports', label: 'Reports', roles: ['admin', 'staff'] },
  { path: '/settings', icon: Settings, key: 'settings', label: 'Settings', roles: ['admin', 'staff'] },
];

export default function MorePage() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const role = user?.role || 'staff';

  const filteredItems = menuItems.filter(item => item.roles.includes(role));

  return (
    <div className="min-h-screen bg-[#F9F8F6] pb-24" data-testid="more-page">
      {/* User Profile Header */}
      <div className="px-5 pt-6 pb-4">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-[#556B2F] flex items-center justify-center shadow-md">
            <span className="text-xl font-bold text-white">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-[#1A2421] truncate">{user?.name || 'User'}</h2>
            <p className="text-sm text-[#5C6656] truncate">{user?.email}</p>
            <span className="inline-block mt-1 px-2.5 py-0.5 text-[11px] rounded-full font-medium capitalize bg-[#E9EDDF] text-[#556B2F]">
              {role === 'superadmin' ? 'Super Admin' : role}
            </span>
          </div>
          <button
            onClick={() => navigate('/settings')}
            className="w-10 h-10 rounded-full bg-[#F0F2EC] flex items-center justify-center"
            data-testid="more-profile-btn"
          >
            <User size={18} className="text-[#5C6656]" />
          </button>
        </div>

        <LanguageSwitcher />
      </div>

      {/* Navigation Grid */}
      <div className="px-5">
        <div className="grid grid-cols-3 gap-3" data-testid="more-nav-grid">
          {filteredItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="flex flex-col items-center gap-2.5 p-4 bg-white rounded-2xl border border-[#E5E7E1] active:bg-[#F5F7F0] active:border-[#C5CDB8] transition-all shadow-sm"
                data-testid={`more-nav-${item.key}`}
              >
                <div className="w-11 h-11 rounded-xl bg-[#F0F2EC] flex items-center justify-center">
                  <Icon size={20} className="text-[#556B2F]" strokeWidth={1.5} />
                </div>
                <span className="text-xs font-medium text-[#1A2421] text-center leading-tight">
                  {t(`sidebar.${item.key}`) !== `sidebar.${item.key}` ? t(`sidebar.${item.key}`) : item.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Notifications & Quick Links */}
      <div className="px-5 mt-6">
        <div className="bg-white rounded-2xl border border-[#E5E7E1] overflow-hidden shadow-sm">
          <button
            onClick={() => navigate('/settings')}
            className="w-full flex items-center gap-3 px-4 py-3.5 active:bg-[#F5F7F0] transition-colors border-b border-[#E5E7E1]"
            data-testid="more-notifications-btn"
          >
            <div className="w-9 h-9 rounded-xl bg-[#FFF3E0] flex items-center justify-center">
              <Bell size={18} className="text-[#E89430]" strokeWidth={1.5} />
            </div>
            <span className="flex-1 text-left text-sm font-medium text-[#1A2421]">Notifications</span>
            <ChevronRight size={16} className="text-[#C5CDB8]" />
          </button>

          {role === 'superadmin' && (
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="w-full flex items-center gap-3 px-4 py-3.5 active:bg-[#F5F7F0] transition-colors border-b border-[#E5E7E1]"
              data-testid="more-superadmin-btn"
            >
              <div className="w-9 h-9 rounded-xl bg-[#E8F5E9] flex items-center justify-center">
                <LayoutDashboard size={18} className="text-[#2E7D32]" strokeWidth={1.5} />
              </div>
              <span className="flex-1 text-left text-sm font-medium text-[#1A2421]">Super Admin Portal</span>
              <ChevronRight size={16} className="text-[#C5CDB8]" />
            </button>
          )}
        </div>
      </div>

      {/* Logout */}
      <div className="px-5 mt-6">
        <button
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl border border-[#FECDD3] bg-[#FFF5F5] text-[#E2725B] active:bg-[#FEE2E2] transition-colors"
          data-testid="more-logout-btn"
        >
          <LogOut size={18} strokeWidth={1.5} />
          <span className="text-sm font-semibold">{t('sidebar.logout')}</span>
        </button>
      </div>

      {/* App version */}
      <div className="text-center mt-6 pb-4">
        <p className="text-[11px] text-[#C5CDB8]">Apptio v2.0</p>
      </div>
    </div>
  );
}
